import {Bindable} from './Bindable';
import util from './Util'
import {config} from './Config';


import coreTranslations from '../core.translations';

'use strict';

/**
 * Provides i18n functionality.
 *
 * Can fire the following events:
 *
 *    > LANGUAGE_CHANGED - fired when language is changed
 *        newLanguage - new language name
 *        previousLanguage - last language name
 *
 * @class Translator
 * @extends Bindable
 * @constructor
 * @module Core
 */
export class Translator extends Bindable {
    _language = null;
    _translations = {};
    _fallbacks = ['en', 'et'];

    constructor() {
        super();
        this.init(coreTranslations, config.language);
    }


    /**
     * Event types.
     *
     * @event
     * @param {Object} Event
     * @param {String} Event.LANGUAGE_CHANGED Triggered when language changes
     */
    Event = {LANGUAGE_CHANGED: 'language-changed'}

    /**
     * Initiates the components.
     *
     * @method init
     * @param {Object} [translations] Translations to use
     * @param {String} [language] Active language
     * @return {Translator} Self
     */
    init(translations, language) {
        if (util.isObject(translations)) {
            this.setTranslations(translations);
        }

        if (util.isString(language)) {
            this.setLanguage(language);
        }

        return this;
    }

    /**
     * Sets base translations to use.
     *
     * @method setTranslations
     * @param {Object} translations Translations to use
     * @return {Translator} Self
     */
    setTranslations(translations) {
        for (let key in translations)
            for (let lang in translations[key])
                this.addTranslation(key, lang, translations[key][lang]);

        return this;
    }

    /**
     * Appends given translations to current, overriding any existing ones with same key+language.
     *
     * @method appendTranslations
     * @param {Object} translations Translations to append
     * @return {Translator} Self
     */
    appendTranslations(module, translations) {
        for (let key in translations)
            for (let lang in translations[key])
                this.addTranslation(module + '.' + key, lang, translations[key][lang]);
        return this;
    }

    // This does not replace any translations already in translator. To be used with lazy-loaded modules that include their own translations
    // (like EAP or future splits of EAK)
    addDefaultTranslations(namespace: string, translations: { [key: string]: { [lang: string]: string } }) {
        for (let key in translations) {
            for (let lang in translations[key]) {
                let fullKey = `${namespace}.${key}`;
                if (!this._translations[fullKey] || !this._translations[fullKey][lang])
                    this.addTranslation(fullKey, lang, translations[key][lang]);
            }
        }
    }

    /**
     * Adds a single translation.
     *
     * @method addTranslation
     * @param {String} key Translation key to add
     * @param {String} language Translation language code
     * @param {String} translation The actual translation in given language
     * @return {Translator} Self
     */
    addTranslation(key, language, translation) {
        if (!util.isObject(this._translations[key])) {
            this._translations[key] = {};
        }

        this._translations[key][language] = translation;

        return this;
    }

    /**
     * Sets active language.
     *
     * @method setLanguage
     * @param {String} language Language to use
     * @return {Translator} Self
     */
    setLanguage(language) {
        var previousLanguage = this._language;

        this._language = language;

        this.fire({
            type: this.Event.LANGUAGE_CHANGED,
            newLanguage: language,
            previousLanguage: previousLanguage
        });

        return this;
    }

    /**
     * Returns active language.
     *
     * @method getLanguage
     * @return {String}
     */
    getLanguage() {
        return this._language;
    }

    getFallbackLanguages() {
        return this._fallbacks.slice();
    }

    /**
     * Returns available languages.
     *
     * If translated is set to true, creates an object and attempts to translate using language.KEY and resulting keys
     * id, name.
     *
     * @method getLanguages
     * @param {Boolean} [translated=false] Should the keys be translated
     * @return {Array}
     */
    getLanguages(translated) {
        translated = util.isBoolean(translated) ? translated : false;

        var languages = [],
            key,
            language;

        for (key in this._translations) {
            for (language in this._translations[key]) {
                if (translated) {
                    languages.push({
                        id: language,
                        name: this.has('lang.' + language) ? this.translate('lang.' + language) : language
                    });
                } else {
                    languages.push(language);
                }
            }

            break;
        }

        return languages;
    }

    /**
     * Returns whether the translator has given translation.
     *
     * @method has
     * @param {String} key Translation key to check
     * @return {Boolean}
     */
    has(key) {
        if (!util.isString(key)) return false;
        return util.isObject(this._translations[key]);
    }

    /**
     * Translates given key.
     *
     * This method accepts any number of arguments that are replaced into the translation string using printf format.
     *
     * @method translate
     * @param {String} key Key to translate
     * @return {String}
     */
    translate(key) {
        if (
            !util.isString(key)
            || !util.isObject(this._translations[key])
        ) {
            return key;
        }

        var translation = this._translations[key][this._language];
        var lang;
        for (lang in this._fallbacks) {
            if (util.isString(translation)) {
                break;
            }
            translation = this._translations[key][this._fallbacks[lang]];
        }

        if (!util.isString(translation)) {
            return 'X:' + key;
        }

        var parameters = _.toArray(arguments);

        parameters[0] = translation;

        return util.sprintf.apply(util, parameters);
    }


    /**
     * Translates given key for given language.
     *
     * This method accepts any number of arguments that are replaced into the translation string using printf format.
     *
     * @method translate
     * @param {String} key Key to translate
     * @param {String} lang Language to translate key in
     * @return {String}
     */
    translateForLanguage(key, lang) {
        if (
            !util.isString(key)
            || !util.isObject(this._translations[key])
        ) {
            return key;
        }

        var translation = this._translations[key][lang];
        var fallback_lang;
        for (fallback_lang in this._fallbacks) {
            if (util.isString(translation)) {
                break;
            }
            translation = this._translations[key][this._fallbacks[fallback_lang]];
        }

        if (!util.isString(translation)) {
            return key;
        }

        var parameters = _.toArray(arguments);

        parameters[0] = translation;

        return util.sprintf.apply(util, parameters);
    }

    translateParam(key, param) {
        var arg = [];
        arg.push(key);
        if (util.isArray(param)) {
            for (var i = 0; i < param.length; i++) {
                arg.push(this.translate(param[i]));
            }
        }
        return this.translate.apply(this, arg);
    }
}

export default new Translator();



// WEBPACK FOOTER //
// ./src/main/ui/core/application/Translator.ts